import { z } from 'zod';
import type { MessageStatus } from './whatsapp';

export const MessageDirection = { INBOUND: 'INBOUND', OUTBOUND: 'OUTBOUND' } as const;
export const MessageSender = { CONTACT: 'CONTACT', BOT: 'BOT', USER: 'USER' } as const;

export const upsertConversationSchema = z.object({
  leadId: z.string().uuid(),
  channel: z.literal('WHATSAPP').default('WHATSAPP'),
  /** Identificador del contacto en el canal (wa_id = número internacional). */
  externalId: z.string().min(3).max(64),
  /** Quién atiende ahora: bot o usuario. */
  assignedToType: z.enum(['BOT', 'USER']).default('BOT'),
  assignedToId: z.string().uuid().optional().nullable(),
  metadata: z.record(z.unknown()).optional(),
});
export type UpsertConversationInput = z.infer<typeof upsertConversationSchema>;
export type UpsertConversationPayload = z.input<typeof upsertConversationSchema>;

export const createMessageSchema = z.object({
  direction: z.nativeEnum(MessageDirection),
  senderType: z.nativeEnum(MessageSender),
  /** Nombre del agente especializado del multiagente (si aplica). */
  agent: z.string().max(80).optional().nullable(),
  text: z.string().max(4000).optional().nullable(),
  template: z.string().max(120).optional().nullable(),
  mediaUrl: z.string().url().max(500).optional().nullable(),
  externalMessageId: z.string().max(120).optional().nullable(),
  sentAt: z.coerce.date().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateMessageInput = z.infer<typeof createMessageSchema>;
export type CreateMessagePayload = z.input<typeof createMessageSchema>;

export interface MessageDto {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  senderType: 'CONTACT' | 'BOT' | 'USER';
  agent: string | null;
  text: string | null;
  template: string | null;
  mediaUrl: string | null;
  sentAt: string;
  metadata: Record<string, unknown>;
  /** Solo salientes por el número propio: entrega, error, categoría (costo) y quién lo envió. */
  status: MessageStatus | null;
  statusAt: string | null;
  error: string | null;
  simulated: boolean;
  templateCategory: string | null;
  sentById: string | null;
}

export interface ConversationDto {
  id: string;
  leadId: string;
  channel: string;
  externalId: string;
  status: 'ABIERTA' | 'CERRADA';
  assignedToType: 'BOT' | 'USER';
  assignedToId: string | null;
  assignedToName: string | null;
  lastMessageAt: string | null;
  /** Total de mensajes de la conversación (no solo los devueltos). */
  messageCount: number;
  /** Los últimos `CONVERSATION_PAGE_SIZE` mensajes, del más antiguo al más reciente. */
  messages: MessageDto[];
  /** true si hay mensajes anteriores al primero devuelto («Ver anteriores»). */
  hasMore: boolean;
}

/** Mensajes que se traen por página en la ficha (los más recientes primero; «Ver anteriores» pide los previos). */
export const CONVERSATION_PAGE_SIZE = 300;

export const olderMessagesQuerySchema = z.object({
  /** Instante ISO (`sentAt`) del mensaje más antiguo ya cargado: se devuelven los anteriores a él. */
  before: z.coerce.date(),
  /**
   * Id de ese mensaje: con varios mensajes en el mismo `sentAt` (marcas de WhatsApp en segundos) el corte es
   * `(sentAt, id)`, así los del borde no se pierden ni se repiten.
   */
  beforeId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(CONVERSATION_PAGE_SIZE).default(CONVERSATION_PAGE_SIZE),
});
export type OlderMessagesQuery = z.infer<typeof olderMessagesQuerySchema>;

export interface MessagesPageDto {
  /** Del más antiguo al más reciente. */
  items: MessageDto[];
  hasMore: boolean;
}
